import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { API_URL_PREFIX, JSON_CONTENT_TYPE_HEADER } from '../../../shared/services/basic.service';

import { ErrorHandlingService } from '../../../shared/services/error-handling.service';
import { TagsService } from '../../../shared/services/tags.service';
import { TaskActionsResponse, TaskActionsRequest } from './model';
import { AnswerAction, TaskOrActionType } from '../../shared/model';
import { ValueAndUnit, Unit } from 'src/app/shared/models/misc.model';

@Injectable({
  providedIn: 'root',
})
export class TaskActionsService {
  constructor(private http: HttpClient, private ehs: ErrorHandlingService, private tagsService: TagsService) {}

  getTaskActions(taskId: number): Observable<TaskActionsResponse> {
    const request: any | TaskActionsRequest = {
      taskId,
    };
    const taskForProcessingURL = `${API_URL_PREFIX}/mobile/task-actions`;
    return this.http.post<TaskActionsResponse>(taskForProcessingURL, request, JSON_CONTENT_TYPE_HEADER).pipe(
      tap(response => this.tagsService.updateTagGroups(response, 'detailedTask')),
      tap(response => this.computeDelaysForUI(response)),
      this.ehs.catchAlertAndThrowHttpError()
    );
  }

  private computeDelaysForUI(response: TaskActionsResponse) {
    for (const action of response.actions.filter(isAnswserAction)) {
      if (action.firstAnswerByHumanDelaysInSeconds) {
        action.firstAnswerByHumanDelays = {};
        const rawDelayS = action.firstAnswerByHumanDelaysInSeconds.raw;
        const bhDelayS = action.firstAnswerByHumanDelaysInSeconds.businessHours;
        if (rawDelayS > 0) {
          action.firstAnswerByHumanDelays.raw = this.delayTermsFromDelaySeconds(rawDelayS);
        }
        if (bhDelayS > 0) {
          action.firstAnswerByHumanDelays.businessHours = this.delayTermsFromDelaySeconds(bhDelayS);
        }
      }
    }
  }

  private delayTermsFromDelaySeconds(delayS: number): ValueAndUnit[] {
    const secondsPerDay = 60 * 60 * 24;
    const secondsPerHour = 60 * 60;
    const secondsPerMinute = 60;
    const terms: ValueAndUnit[] = [];
    const j = Math.floor(delayS / secondsPerDay);
    if (j > 0) {
      terms.push({ value: j, unit: Unit.Day });
    }
    const h = Math.floor((delayS - j * secondsPerDay) / secondsPerHour);
    if (h > 0 || j > 0) {
      terms.push({ value: h, unit: Unit.Hour });
    }
    const m = Math.floor((delayS - j * secondsPerDay - h * secondsPerHour) / secondsPerMinute);
    // we keep the two most significative terms
    if (j === 0 && (m > 0 || h > 0)) {
      terms.push({ value: m, unit: Unit.Minute });
    }
    const s = delayS - j * secondsPerDay - h * secondsPerHour - m * secondsPerMinute;
    if (j === 0 && h === 0) {
      terms.push({ value: s, unit: Unit.Second });
    }
    return terms;
  }
}

// Type predicate
function isAnswserAction(action): action is AnswerAction {
  return action.type === TaskOrActionType.Answer;
}
